import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { EMPTY_LINE, LineEditor } from "./LineEditor";
import { createDoc, listCustomers } from "../../api/sales";
import { localName } from "../../lib/localName";

const today = () => new Date().toISOString().slice(0, 10);
const plus30 = () => new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

// إنشاء عرض سعر جديد: عميل + تواريخ + سطور، والتسعير والضريبة تُحسب في الخادم.
export const QuotationBuilder = ({ onCreated }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [head, setHead] = useState({ customer_id: "", date: today(), valid_until: plus30(),
                                      discount_percent: "", reference: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listCustomers({ limit: 200 }).then((data) => setCustomers(data.items || [])).catch(() => setCustomers([]));
  }, []);

  const changeLine = (index, next) => setLines(lines.map((l, i) => (i === index ? next : l)));
  const removeLine = (index) => setLines(lines.filter((_, i) => i !== index));

  const save = async () => {
    setBusy(true);
    try {
      const doc = await createDoc({
        header: {
          doc_type: "quotation", date: head.date, valid_until: head.valid_until,
          customer_id: head.customer_id, reference: head.reference || null,
          discount_percent: head.discount_percent || null,
        },
        lines: lines.map((line, index) => ({ ...line, line_no: index + 1 })),
      });
      toast.success(`${t("sales.quotationCreated")} — ${doc.totals.payable} ${doc.money.currency}`);
      setLines([{ ...EMPTY_LINE }]);
      onCreated?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.code || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard className="grid gap-3 p-5" data-testid="quotation-builder">
      <p className="label-xs">{t("sales.newQuotation")}</p>
      <div className="grid gap-3 sm:grid-cols-5">
        <NeuSelect data-testid="quote-customer" value={head.customer_id}
                   onChange={(e) => setHead({ ...head, customer_id: e.target.value })}>
          <option value="">{t("sales.pickCustomer")}</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{localName(c)}</option>
          ))}
        </NeuSelect>
        <NeuInput data-testid="quote-date" type="date" value={head.date}
                  onChange={(e) => setHead({ ...head, date: e.target.value })} />
        <NeuInput data-testid="quote-valid" type="date" value={head.valid_until}
                  onChange={(e) => setHead({ ...head, valid_until: e.target.value })} />
        <NeuInput data-testid="quote-discount" dir="ltr" placeholder={t("sales.docDiscount")}
                  value={head.discount_percent}
                  onChange={(e) => setHead({ ...head, discount_percent: e.target.value })} />
        <NeuInput data-testid="quote-reference" dir="ltr" placeholder={t("sales.customerRef")}
                  value={head.reference}
                  onChange={(e) => setHead({ ...head, reference: e.target.value })} />
      </div>

      <div className="grid gap-2">
        {lines.map((line, index) => (
          <LineEditor key={index} line={line} index={index} onChange={changeLine} onRemove={removeLine} />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <NeuButton className="px-3 py-2 text-xs" data-testid="quote-add-line"
                   onClick={() => setLines([...lines, { ...EMPTY_LINE }])}>
          <Plus className="h-3.5 w-3.5" /> {t("sales.addLine")}
        </NeuButton>
        <NeuButton variant="primary" data-testid="quote-save" disabled={busy || !head.customer_id}
                   onClick={save}>
          {t("sales.createQuotation")}
        </NeuButton>
      </div>
    </NeuCard>
  );
};
